import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { task } from 'ember-concurrency';
import { or, and, not } from 'ember-truth-helpers';
import { t } from 'ember-intl';
import { queue, pick } from '@nullvoxpopuli/ember-composable-helpers';
import set from 'ember-set-helper/helpers/set';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsTextBody,
  HdsFormTextInputField,
  HdsFormTextareaField,
  HdsAlert,
  HdsButton,
  HdsButtonSet,
} from '@hashicorp/design-system-components/components';
import {
  CoreLayoutMain,
  BreadcrumbsContainer,
  LimitBanner,
  Flex,
  FlexGrid,
  WithErrors,
} from 'core/components';
import { resourceTypeFor } from 'core/helpers';
import { boxMargin, boxPadding } from 'core/modifiers';

export const MIN_PROJECT_NAME_LENGTH = 3;

/**
 * The current organization's projects list.
 * @argument projects
 * @type {Array}
 */
/**
 *
 * Create form for a new project.
 *
 *
 * ```
 * <Page::Projects::Create
 *   @organization={{this.model.organization}}
 *   @projects={{array project1 project2}}
 * />
 * ```
 *
 * @class PageProjectsCreate
 *
 */
export default class PageProjectsCreate extends Component {
  @service api;
  @service router;
  @service('resources.project') projects;
  @service intl;
  @service flashMessages;

  @tracked name = '';
  @tracked nameIsPristine = true;
  @tracked description = '';
  @tracked validationError;

  save = task(async (evt) => {
    // don't submit the form
    evt.preventDefault();

    this.nameIsPristine = false;

    await this.validate.perform();

    if (this.validationError) {
      return;
    }
    const { organization } = this.args;

    const { name, description } = this;
    const project = await this.projects.createProject.perform(
      name,
      description,
      organization
    );

    this.onProjectCreateSuccess(project);
  });

  validate = task(async () => {
    const { name } = this;
    const noSpecialCharactersRegex = /[^a-zA-Z0-9\s_\-.]/;

    try {
      const validationError = new Error();
      validationError.details = [];

      // Check that project name is not blank
      if (!name || !name.length) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-cannot-be-blank-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name is greater than the set minimum length
      if (name && name.length < MIN_PROJECT_NAME_LENGTH) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-must-be-minimum-length-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name does not contain disallowed special characters
      if (name && noSpecialCharactersRegex.test(name)) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-contains-disallowed-characters-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name does not begin with a space
      if (name && name.startsWith(' ')) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-begins-with-space-error'
                ),
              },
            ],
          },
        ];
      }

      // Check that project name does not end with a space
      if (name && name.endsWith(' ')) {
        validationError.details = [
          ...validationError.details,
          {
            field_violations: [
              {
                field: `name`,
                description: this.intl.t(
                  'components.page.projects.create.form.label.project-name-ends-with-space-error'
                ),
              },
            ],
          },
        ];
      }

      if (validationError.details.length) {
        throw validationError;
      } else {
        await (this.validationError = undefined);
      }
    } catch (e) {
      this.validationError = e;
    }
  });

  onProjectCreateSuccess(project) {
    this.flashMessages.success(
      this.intl.t('components.page.projects.create.project-created'),
      {
        content: this.intl.t(
          'components.page.projects.create.project-has-been-created',
          {
            projectName: project.name,
          }
        ),
        actionText: this.intl.t(
          'components.page.projects.create.go-to-project'
        ),
        onAction: () => {
          return this.router.transitionTo(
            'cloud.orgs.detail.projects.detail',
            project.id
          );
        },
      }
    );
    this.router.transitionTo('cloud.orgs.detail.projects.list');
  }
  <template>
    <WithErrors
      @error={{or this.validationError this.save.last.error}}
      as |FormError FieldError|
    >
      <CoreLayoutMain>
        <:header>
          <HdsPageHeader as |PH|>
            <PH.Breadcrumb>
              <BreadcrumbsContainer />
            </PH.Breadcrumb>
            <PH.Title>
              {{t
                "components.page.projects.create.title"
                orgName=@organization.name
              }}
            </PH.Title>
          </HdsPageHeader>
        </:header>
        <:alert>
          <LimitBanner
            @count={{@projects.length}}
            @variant={{resourceTypeFor "project"}}
            @padding="0"
          />
          {{#if this.save.last.error}}
            <FormError @shouldRenderPageError={{true}} />
          {{/if}}
        </:alert>
        <:body>
          <FlexGrid as |G|>
            <G.Item @xs="12" @sm="12" @md="8" @lg="5">
              <form
                class="hcp-hds-form"
                data-test-form
                {{on "submit" this.save.perform}}
              >
                <Flex @direction="column" @gap="xl">
                  <HdsTextBody @color="strong" @size="200" @weight="regular">
                    {{t "components.page.projects.create.text"}}
                  </HdsTextBody>

                  {{! HCP project name }}
                  <div>
                    <FieldError
                      @field="name"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      {{#let
                        (and (not this.nameIsPristine) err)
                        as |isInvalid|
                      }}
                        <HdsFormTextInputField
                          @id="projectName"
                          @isRequired={{true}}
                          @isInvalid={{isInvalid}}
                          @type="text"
                          @value={{this.name}}
                          name="projectName"
                          data-test-project-name
                          data-test-input
                          {{on "blur" (set this "nameIsPristine" false)}}
                          {{on
                            "input"
                            (queue
                              (pick "target.value" (set this "name"))
                              this.validate.perform
                            )
                          }}
                          as |F|
                        >
                          <F.Label>
                            {{t
                              "components.page.projects.create.form.label.project-name"
                            }}
                          </F.Label>
                          <F.HelperText>
                            <ul class="input-requirements">
                              <li>
                                {{t
                                  "components.page.projects.create.form.label.project-name-may-contain"
                                }}
                              </li>
                              <li>
                                {{t
                                  "components.page.projects.create.form.label.project-name-cannot-begin-with"
                                }}
                              </li>
                            </ul>
                          </F.HelperText>
                          <F.CharacterCount
                            @minLength={{3}}
                            @maxLength={{40}}
                          />
                          {{#if isInvalid}}
                            <F.Error data-test-project-name-error>
                              {{err.description}}
                            </F.Error>
                          {{/if}}
                        </HdsFormTextInputField>
                      {{/let}}
                    </FieldError>
                  </div>

                  {{! HCP project description }}
                  <div>
                    <FieldError
                      @field="description"
                      @hdsDelegatedError={{true}}
                      as |err|
                    >
                      <HdsFormTextareaField
                        @id="projectDescription"
                        @isInvalid={{err}}
                        @type="text"
                        @value={{this.description}}
                        name="projectDescription"
                        @isOptional={{true}}
                        data-test-project-description
                        data-test-input
                        {{on
                          "input"
                          (pick "target.value" (set this "description"))
                        }}
                        as |F|
                      >
                        <F.Label>
                          {{t
                            "components.page.projects.create.form.label.project-description"
                          }}
                        </F.Label>
                        <F.HelperText>
                          {{t
                            "components.page.projects.create.form.label.project-description-help"
                          }}
                        </F.HelperText>
                        <F.CharacterCount @minLength={{0}} @maxLength={{256}} />
                        {{#if err}}
                          <F.Error>
                            {{err.description}}
                          </F.Error>
                        {{/if}}
                      </HdsFormTextareaField>
                    </FieldError>
                    <div {{boxMargin "lg 0 0 0"}}>
                      <HdsAlert
                        @color="highlight"
                        @icon="info-fill"
                        @type="compact"
                        as |A|
                      >
                        <A.Description>
                          {{t
                            "components.page.projects.create.resources-are-scoped-to-project"
                          }}
                        </A.Description>
                      </HdsAlert>
                    </div>
                  </div>
                </Flex>
                <footer {{boxPadding "xl 0 0 0"}}>
                  <section>
                    <HdsButtonSet>
                      {{#if (can "create project" @projects)}}
                        <HdsButton
                          @color="primary"
                          @text={{t "components.page.projects.create.action"}}
                          disabled={{this.save.isRunning}}
                          type="submit"
                          data-test-submit
                        />
                      {{/if}}
                      {{!
                  Disable this button while submitting because there are
                  multiple requests firing that rely on each other. Allowing a
                  cancel while submitting could lead to broken project/billing
                  attachment.
                  }}
                      <HdsButton
                        @color="secondary"
                        @route="cloud.orgs.detail.projects.list"
                        @text={{t "components.form.cancel"}}
                        disabled={{this.save.isRunning}}
                        data-test-cancel
                      />
                    </HdsButtonSet>
                  </section>
                </footer>
              </form>
            </G.Item>
          </FlexGrid>
        </:body>
      </CoreLayoutMain>
    </WithErrors>
  </template>
}
